import type {
	NotificationPreferencesDTO,
	UpdateNotificationPreferencesBody,
} from "@zplanem/responses"
import { useEffect, useMemo, useState } from "react"
import { Button } from "@/components/ui/button"

interface PropertyOption {
	id: string
	name: string
}

interface NotificationPreferencesCopy {
	title: string
	masterToggle: string
	quietHoursToggle: string
	quietHoursStart: string
	quietHoursEnd: string
	propertiesTitle: string
	muteProperty: (name: string) => string
	save: string
	savedTimeZone: string
	loading: string
	error: string
}

interface NotificationPreferencesSectionProps {
	copy: NotificationPreferencesCopy
	currentTimeZone: string
	error?: string | null
	onSave: (body: UpdateNotificationPreferencesBody) => Promise<void> | void
	preferences: NotificationPreferencesDTO | null | undefined
	properties: readonly PropertyOption[]
	saving: boolean
}

function sortedUnique(values: readonly string[]): string[] {
	return [...new Set(values)].sort()
}

function samePayload(
	left: UpdateNotificationPreferencesBody,
	right: UpdateNotificationPreferencesBody,
): boolean {
	return (
		left.masterEnabled === right.masterEnabled &&
		left.quietHours.enabled === right.quietHours.enabled &&
		left.quietHours.start === right.quietHours.start &&
		left.quietHours.end === right.quietHours.end &&
		left.quietHours.timeZone === right.quietHours.timeZone &&
		JSON.stringify(sortedUnique(left.mutedPropertyIds)) ===
			JSON.stringify(sortedUnique(right.mutedPropertyIds))
	)
}

function payloadFromPreferences(
	preferences: NotificationPreferencesDTO,
): UpdateNotificationPreferencesBody {
	return {
		masterEnabled: preferences.masterEnabled,
		mutedPropertyIds: sortedUnique(preferences.mutedPropertyIds),
		quietHours: preferences.quietHours,
	}
}

export function NotificationPreferencesSection({
	copy,
	currentTimeZone,
	error,
	onSave,
	preferences,
	properties,
	saving,
}: NotificationPreferencesSectionProps) {
	const initial = useMemo(
		() => (preferences ? payloadFromPreferences(preferences) : null),
		[preferences],
	)
	const [draft, setDraft] = useState<UpdateNotificationPreferencesBody | null>(
		initial,
	)

	useEffect(() => {
		setDraft(initial)
	}, [initial])

	if (!preferences || !draft) {
		return (
			<section aria-label={copy.title} className="space-y-3">
				<h2 className="text-xl font-bold text-foreground">{copy.title}</h2>
				{error ? (
					<div
						role="alert"
						className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
					>
						{error}
					</div>
				) : (
					<p className="text-sm text-muted-foreground">{copy.loading}</p>
				)}
			</section>
		)
	}

	const muted = new Set(draft.mutedPropertyIds)
	const dirty = !samePayload(draft, payloadFromPreferences(preferences))

	function toggleMutedProperty(propertyId: string, enabled: boolean) {
		setDraft((current) => {
			if (!current) return current
			const next = new Set(current.mutedPropertyIds)
			if (enabled) {
				next.add(propertyId)
			} else {
				next.delete(propertyId)
			}
			return { ...current, mutedPropertyIds: sortedUnique([...next]) }
		})
	}

	function save() {
		if (!draft || !dirty || saving) return
		void onSave({
			...draft,
			mutedPropertyIds: sortedUnique(draft.mutedPropertyIds),
			quietHours: { ...draft.quietHours, timeZone: currentTimeZone },
		})
	}

	return (
		<section aria-label={copy.title} className="space-y-5">
			<div className="space-y-1">
				<h2 className="text-xl font-bold text-foreground">{copy.title}</h2>
				<p className="text-sm text-muted-foreground">
					{copy.savedTimeZone}: {preferences.quietHours.timeZone}
				</p>
			</div>

			<label className="flex items-center justify-between gap-4 rounded-md border border-border px-4 py-3">
				<span className="text-sm font-semibold text-foreground">
					{copy.masterToggle}
				</span>
				<input
					type="checkbox"
					checked={draft.masterEnabled}
					onChange={(event) =>
						setDraft((current) =>
							current
								? { ...current, masterEnabled: event.target.checked }
								: current,
						)
					}
					className="size-5 accent-primary"
				/>
			</label>

			<div className="space-y-3">
				<label className="flex items-center justify-between gap-4 rounded-md border border-border px-4 py-3">
					<span className="text-sm font-semibold text-foreground">
						{copy.quietHoursToggle}
					</span>
					<input
						type="checkbox"
						checked={draft.quietHours.enabled}
						onChange={(event) =>
							setDraft((current) =>
								current
									? {
											...current,
											quietHours: {
												...current.quietHours,
												enabled: event.target.checked,
											},
										}
									: current,
							)
						}
						className="size-5 accent-primary"
					/>
				</label>

				<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
					<label className="space-y-1 text-sm font-medium text-foreground/80">
						<span>{copy.quietHoursStart}</span>
						<input
							type="time"
							value={draft.quietHours.start}
							onChange={(event) =>
								setDraft((current) =>
									current
										? {
												...current,
												quietHours: {
													...current.quietHours,
													start: event.target.value,
												},
											}
										: current,
								)
							}
							className="w-full rounded-md border border-border bg-muted px-3 py-2 text-foreground outline-none focus:border-ring"
						/>
					</label>
					<label className="space-y-1 text-sm font-medium text-foreground/80">
						<span>{copy.quietHoursEnd}</span>
						<input
							type="time"
							value={draft.quietHours.end}
							onChange={(event) =>
								setDraft((current) =>
									current
										? {
												...current,
												quietHours: {
													...current.quietHours,
													end: event.target.value,
												},
											}
										: current,
								)
							}
							className="w-full rounded-md border border-border bg-muted px-3 py-2 text-foreground outline-none focus:border-ring"
						/>
					</label>
				</div>
			</div>

			<div className="space-y-3">
				<h3 className="text-sm font-semibold text-foreground/80">
					{copy.propertiesTitle}
				</h3>
				<div className="divide-y divide-border rounded-md border border-border">
					{properties.map((property) => (
						<label
							key={property.id}
							className="flex items-center justify-between gap-4 px-4 py-3"
						>
							<span className="min-w-0 text-sm text-foreground">
								{property.name}
							</span>
							<input
								type="checkbox"
								checked={muted.has(property.id)}
								aria-label={copy.muteProperty(property.name)}
								onChange={(event) =>
									toggleMutedProperty(property.id, event.target.checked)
								}
								className="size-5 shrink-0 accent-primary"
							/>
						</label>
					))}
				</div>
			</div>

			{error ? (
				<div
					role="alert"
					className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
				>
					{error}
				</div>
			) : null}

			<Button
				type="button"
				variant="brand"
				disabled={!dirty || saving}
				onClick={save}
			>
				{copy.save}
			</Button>
		</section>
	)
}
